/**
 * Consume permisos efectivos del usuario autenticado desde el backend.
 *
 * Códigos MENU: `nav-item.model.ts` (`NAV_ITEMS`, prefijo `menu.*`).
 * Códigos ACCIÓN: `permiso-accion.constants.ts` (`PERMISO_ACCION`).
 */
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { buildColmenaApiUrl } from '../../../core/api-url';
import { PERMISO_ACCION } from '../constants/permiso-accion.constants';
import { DASHBOARD_ROUTE } from '../models/nav-item.model';
import { AuthService } from './auth.service';

/** Reexport para consumidores que importan solo desde el servicio. */
export { PERMISO_ACCION };

const NO_CACHE_HEADERS = new HttpHeaders({
  'Cache-Control': 'no-cache, no-store, must-revalidate',
  Pragma: 'no-cache',
});

interface ApiEnvelope<T> {
  success: boolean;
  data: T;
}

export interface PermisosMeResponse {
  usuario: {
    id: string;
    sub: string;
    email: string | null;
    nombre: string | null;
    keycloakGroups: string[];
  };
  permisos: Array<{ codigo: string; nombre: string; tipo: string; recurso: string }>;
  codigos: string[];
}

@Injectable({ providedIn: 'root' })
export class PermisosService {
  private readonly http = inject(HttpClient);
  private readonly authService = inject(AuthService);
  private readonly codigos = signal<string[]>([]);
  private readonly usuarioId = signal<string | null>(null);
  private readonly loaded = signal(false);

  readonly permisoCodigos = this.codigos.asReadonly();

  clear(): void {
    this.codigos.set([]);
    this.usuarioId.set(null);
    this.loaded.set(false);
  }

  /** UUID persistido en `mst_usuario` tras `GET /auth/permisos/me` (FK en acciones de negocio). */
  getUsuarioId(): string | null {
    return this.usuarioId();
  }

  /** Carga permisos desde `GET /auth/permisos/me` (requiere Bearer). */
  async loadPermisos(): Promise<void> {
    if (!this.authService.isAuthenticated()) {
      this.codigos.set([]);
      this.usuarioId.set(null);
      this.loaded.set(false);
      return;
    }

    const tokenReady = await this.authService.ensureValidAccessToken();
    if (!tokenReady) {
      throw new HttpErrorResponse({
        status: 401,
        error: { error: { message: 'jwt expired' } },
      });
    }

    const url = buildColmenaApiUrl('/auth/permisos/me');
    const response = await firstValueFrom(
      this.http.get<ApiEnvelope<PermisosMeResponse>>(url, {
        headers: NO_CACHE_HEADERS,
        // Evita reutilización condicional (304 sin cuerpo) en algunos navegadores.
        params: { _nc: Date.now().toString() },
      }),
    );
    if (!response?.success || !response.data) {
      throw new Error('No se recibieron permisos del servidor. Recargue e intente de nuevo.');
    }
    this.codigos.set(response.data.codigos ?? []);
    this.usuarioId.set(response.data.usuario?.id?.trim() || null);
    this.loaded.set(true);
  }

  isLoaded(): boolean {
    return this.loaded();
  }

  /**
   * Indica si el usuario posee el código (MENU o ACCIÓN) en la sesión actual.
   *
   * @param codigo p. ej. `menu.dashboard` o `archivo.anular`.
   */
  hasPermiso(codigo: string): boolean {
    return this.codigos().includes(codigo);
  }

  canAccessRoute(route: string): boolean {
    const normalized = route.split('?')[0].replace(/\/$/, '') || '/';
    // Home siempre accesible con sesión válida (evita bucle de guard si aún no hay menús).
    if (normalized === '/' || normalized === '' || normalized === DASHBOARD_ROUTE) {
      return true;
    }
    const menuPermisos = this.codigos().filter((c) => c.startsWith('menu.'));
    for (const codigo of menuPermisos) {
      const item = NAV_ROUTE_BY_CODIGO.get(codigo);
      if (!item) {
        continue;
      }
      if (item === normalized || normalized.startsWith(`${item}/`)) {
        return true;
      }
    }
    return false;
  }
}

/** Mapa auxiliar código → ruta base (sincronizado con seeds backend). */
const NAV_ROUTE_BY_CODIGO = new Map<string, string>([
  ['menu.home', DASHBOARD_ROUTE],
  ['menu.dashboard', DASHBOARD_ROUTE],
  ['menu.cargar-ingresos', '/cargar-ingresos'],
  ['menu.mockup', '/cargar-ingresos-mockup'],
  ['menu.confirmacionPago', '/confirmacion-pago'],
  ['menu.matriz', '/matriz-recaudacion'],
  ['menu.procesamiento', '/procesamiento'],
  ['menu.reportes-trazabilidad', '/reportes/trazabilidad-usuarios'],
  ['menu.motor-reglas-reglas', '/motor-reglas/reglas-recaudadores'],
  ['menu.motor-reglas-errores', '/motor-reglas/errores-archivos'],
  ['menu.configuracion-mantenedores', '/colmena/configuracion'],
  ['menu.configuracion-recaudador', '/configuracion-recaudador'],
  ['menu.configuracion-layout-archivos', '/configuracion-layout-archivos'],
  ['menu.configuracion-recaudador-mockup', '/configuracion-recaudador-mockup'],
]);
